import { CSSProperties, forwardRef, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { FulfillmentPriorityShop } from "../../../../../../@types/FulfillmentPriority";
import { SwitchButton } from "../../../../../../components/Switch";

import styles from "./ShopCard.module.css";

interface ShopCardProps {
  shop: FulfillmentPriorityShop;
  disabled?: boolean;
  dragHandle?: ReactNode;
  isDragging?: boolean;
  style?: CSSProperties;
  /** Omit to render the status switch as read-only. */
  onStatusChange?: (shop: FulfillmentPriorityShop) => void;
}

export const ShopCard = forwardRef<HTMLElement, ShopCardProps>(
  function ShopCard(
    {
      shop,
      disabled = false,
      dragHandle,
      isDragging = false,
      style,
      onStatusChange,
    },
    ref,
  ) {
    const { t } = useTranslation();
    const cardClassName = [
      styles.shopCard,
      dragHandle ? styles.withDragHandle : "",
      isDragging ? styles.dragging : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <article ref={ref} className={cardClassName} style={style}>
        {dragHandle}

        <span
          className={`${styles.priorityBadge} ${
            !shop.isActive ? styles.inactive : ""
          }`}
          aria-label={t("priorityNumber", { priority: shop.priority })}
        >
          {shop.priority}
        </span>

        <div className={styles.shopDetails}>
          <h3>{shop.name}</h3>
          <p>
            {shop.tenantName}
            <i className={styles.separator} aria-hidden="true" />
            <span className={!shop.isActive ? styles.inactivePriority : ""}>
              {t("priority")}: #{shop.priority}
            </span>
          </p>
        </div>

        <div className={styles.statusControl}>
          <SwitchButton
            ariaLabel={t("toggleFulfillmentLocationStatus", {
              name: shop.name,
            })}
            checked={shop.isActive}
            disabled={disabled || !onStatusChange}
            onChange={() => onStatusChange?.(shop)}
            offColor="#CBD5E1"
          />
          <span className={!shop.isActive ? styles.inactiveStatus : ""}>
            {t(shop.isActive ? "active" : "inactive")}
          </span>
        </div>
      </article>
    );
  },
);
